import { Injectable } from '@angular/core';
import {
  CreateSurveyOverview,
  SurveyOverview,
  UpdateSurveyOverview,
} from '../models/survey-overview.module';
import { BaseAWSRequestService } from '../../shared/service/base-services/base-aws-request.service';
import {
  SurveyConnection,
  SurveyFilterInput,
  SurveyCreateInput,
  CreateSurveyMutationVariables,
  CreateSurveyMutation,
  SurveyUpdateInput,
  UpdateSurveyMutation,
  UpdateSurveyMutationVariables,
  Survey,
  GetSurveyByNameQueryVariables,
  GetSurveyByNameQuery,
  GetAllSurveyLaunchpadQueryVariables,
  AssignUserToSurveyInput,
  SurveyResponse,
  AssignUserToSurveyMutationVariables,
  AssignUserToSurveyMutation,
  CreateSurveyFromRouteMutation,
  CreateSurveyFromRouteMutationVariables,
  RenameSurveyInput,
  RenameSurveyMutation,
  RenameSurveyMutationVariables,
  ResetSurveyInput,
  DeleteSurveyMutation,
  DeleteSurveyMutationVariables,
} from '../../../awsAppSync/API';

import {
  getAllSurveyLaunchpad,
  getSurveyByName,
} from '../../../awsAppSync/graphql/queries';

import {
  CREATE_SURVEY_ERROR,
  CREATE_SURVEY_SUCCESS,
  CREATE_SURVEY_IN_PROGRESS,
  CREATE_SURVEY_STILL_PROCESSING,
  SURVEY_CREATION_STATUS_TIMEOUT_MS,
  ERROR,
  ERROR_FETCHING_SURVEY_OVERVIEW,
  FAILED_CREATE_SURVEY,
  INFO,
  REDIRECT_SURVEY_OVERVIEW,
  SUCCESS,
  TOASTER_SUCCESS,
  UPDATE_SURVEY_SUCCESS,
  ASSIGN_USER_TO_SURVEY_ERROR,
} from '../../shared/constants';
import { Assets } from '../models/assets';
import {
  createSurvey,
  createSurveyFromRoute,
  updateSurvey,
  assignUserToSurvey,
  renameSurvey,
  deleteSurvey,
} from '../../../awsAppSync/graphql/mutations';
import { onUpdateSurveyCreationStatus } from '../../../awsAppSync/graphql/subscriptions';
import { Router } from '@angular/router';
import { CustomToastrService } from '../../shared/ngx-toastr/custom-toastr.service';
import { Store } from '@ngxs/store';
import { AuthenticateUserState } from '../store/authenticate-user.state';
import { generateClient } from 'aws-amplify/api';
import { Observable } from 'rxjs';

/**
 * SYRC-6904 (Option B): describes a backend-driven route selection. When present, the
 * survey is created from the route's server-resolved assets (minus the excluded ids)
 * rather than a frontend-supplied asset array.
 */
export interface SurveyRouteSelection {
  surveyRouteId: string;
  excludedAssetIds: string[];
}

/**
 * Describes a backend-driven hierarchy filter selection. When present, the
 * survey is created from the backend-resolved hierarchy assets (minus excluded ids)
 * rather than a frontend-supplied asset array.
 */
export interface SurveyHierarchySelection {
  hierarchyFilterInputs: { level1: string; level2: string; level3: string }[];
  gridFilters?: any;
  excludedAssetIds: string[];
  surveyFrequencyType?: string;
  assetTypes?: string[];
  segmentTypes?: string[];
  level4?: string[];
}

/**
 * SYRC-6904: terminal status pushed by the survey-create worker over the
 * onUpdateSurveyCreationStatus subscription.
 */
export interface SurveyCreationStatus {
  userId: string;
  jobId?: string | null;
  status: string;
  surveyId?: string | null;
  message?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class SurveyOverviewService {
  private storageKey = 'surveyData';
  constructor(
    private awsService: BaseAWSRequestService,
    private router: Router,
    private toastr: CustomToastrService,
    private store: Store,
  ) {}
  generateRandomData(count: number): SurveyOverview[] {
    // Retrieve existing data from local storage
    const storedData = localStorage.getItem(this.storageKey);
    let existingData: SurveyOverview[] = [];

    if (storedData) {
      // If data exists in local storage, parse it
      existingData = JSON.parse(storedData);
    }

    // Generate new random data
    const surveyNames = ['Survey Name 1', 'Survey Name 2', 'Survey Name 3'];
    const surveyTypes = [
      '2021 Annual Survey',
      '2022 Mid-Year Survey',
      '2023 End-Year Survey',
    ];
    const priorities = ['High', 'Medium', 'Low'];
    const statuses = [
      'Inspection Assigned',
      'Inspection Underway',
      'Inspection Verified',
      'Inspection Review',
    ];
    const collectionTypes = ['Compliance', 'Exception', 'Maintenance'];
    const surveyFrequenyTypes = ['Annual', ' Periodic', 'Supplemental'];
    const technicians = ['Tiger Woods', 'John Doe', 'Jane Smith'];
    const supervisors = ['Lorem ipsum', 'Dolor sit', 'Amet consectetur'];
    const descriptions = [
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit',
      'Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua',
      'Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris',
    ];

    // Calculate how many new entries need to be generated
    const newEntriesCount = count - existingData.length;

    for (let i = 0; i < newEntriesCount; i++) {
      const randomSurveyName =
        surveyNames[Math.floor(Math.random() * surveyNames.length)];
      const randomSurveyType =
        surveyTypes[Math.floor(Math.random() * surveyTypes.length)];
      const randomPriority =
        priorities[Math.floor(Math.random() * priorities.length)];
      const randomStatus =
        statuses[Math.floor(Math.random() * statuses.length)];
      const randomDate = new Date(2023, 0, 1);
      const randomCollectionType =
        collectionTypes[Math.floor(Math.random() * collectionTypes.length)];
      const randomsurveyFrequencyType =
        surveyFrequenyTypes[
          Math.floor(Math.random() * surveyFrequenyTypes.length)
        ];
      const randomTechnician =
        technicians[Math.floor(Math.random() * technicians.length)];
      const randomSupervisor =
        supervisors[Math.floor(Math.random() * supervisors.length)];
      const randomDescription =
        descriptions[Math.floor(Math.random() * descriptions.length)];

      existingData.push({
        id: (existingData.length + 1).toString(),
        surveyName: randomSurveyName,
        surveyType: randomSurveyType,
        surveyPriority: randomPriority,
        surveyStatus: randomStatus,
        dueDate: randomDate,
        dataCollectionType: randomCollectionType,
        surveyFrequencyType: randomsurveyFrequencyType,
        technician: randomTechnician,
        supervisor: randomSupervisor,
        dateOfCompletion: randomDate,
        surveyDescription: randomDescription,
      });
    }

    // Save the combined data back to local storage
    localStorage.setItem(this.storageKey, JSON.stringify(existingData));

    return existingData;
  }

  async addSurveyEntry(
    newEntry: CreateSurveyOverview,
    assets: Assets[],
    routeSelection?: SurveyRouteSelection,
    hierarchySelection?: SurveyHierarchySelection,
  ): Promise<void> {
    const dueDate = new Date(newEntry.dueDate);
    dueDate.setHours(0, 0, 0, 0);
    const assignedToValues = {
      id: newEntry.assignedTo,
      name: newEntry.assignedToValue,
    };
    const inputObject: SurveyCreateInput = {
      primaryCompanyId: newEntry.primaryCompanyId,
      surveyName: newEntry.surveyName,
      surveyType: newEntry.surveyType,
      surveyPriority: newEntry.surveyPriority,
      dueDate: dueDate.toISOString(),
      dataCollectionType: newEntry.dataCollectionType,
      surveyFrequencyType: newEntry.surveyFrequencyType,
      surveyDescription: newEntry.surveyDescription,
      assignedTo: assignedToValues,
      troubleShooting: this.toTroubleShootingString(newEntry.troubleshooting),
      filter: newEntry.filter,
      // SYRC-6904 (Option B): when a route is selected and the user hasn't manually
      // reordered/removed assets, defer asset resolution to the backend by sending the
      // route id + any deselected ids — instead of shipping the full (possibly huge) list.
      ...(routeSelection
        ? {
            surveyRouteId: routeSelection.surveyRouteId,
            excludedAssetIds: routeSelection.excludedAssetIds,
          }
        : hierarchySelection
        ? {
            hierarchyFilterInputs: hierarchySelection.hierarchyFilterInputs,
            gridFilters: hierarchySelection.gridFilters,
            excludedAssetIds: hierarchySelection.excludedAssetIds,
            assetTypes: hierarchySelection.assetTypes,
            segmentTypes: hierarchySelection.segmentTypes,
            level4: hierarchySelection.level4,
          }
        : {
            surveyAssociatedAssetInput: assets.map((asset) => ({
              assetId: asset.id ?? '',
              level1ValueId: asset.level1Id ?? '',
              level2ValueId: asset.level2Id ?? '',
              level3ValueId: asset.level3Id ?? '',
              order: asset.order ?? null,
            })),
          }),
    };
    // SYRC-6904: a route selection resolves a (potentially huge) asset set on the backend, which can
    // exceed AppSync's ~30s synchronous ceiling. Dispatch those asynchronously and report completion
    // via the onUpdateSurveyCreationStatus subscription. Small manual surveys stay synchronous.
    // Hierarchy "Select All" also uses the async path — the backend resolves assets from the filter
    // server-side, which can exceed the sync budget for large hierarchies (same pattern as routes).
    if (routeSelection || hierarchySelection) {
      await this.createSurveyFromRouteAsync(inputObject);
      return;
    }

    const response = await this.createSurvey(inputObject);
    if (response?.createSurvey?.status?.toLocaleLowerCase() == SUCCESS) {
      this.toastr.showSuccess(CREATE_SURVEY_SUCCESS, TOASTER_SUCCESS);
      this.router.navigate([REDIRECT_SURVEY_OVERVIEW]);
    } else {
      const errorMessage = response?.createSurvey?.error?.message || FAILED_CREATE_SURVEY;
      this.toastr.showError(errorMessage, ERROR);
    }
  }

  /**
   * troubleShooting is a String field, but the survey entry defaults it to a boolean when the form
   * omits it, which GraphQL rejects ("String cannot represent a non string value: false"). Coerce any
   * non-string to null so both the sync createSurvey and async createSurveyFromRoute inputs validate.
   * (Upstream fix: the form should default the field to a string/null, not boolean false.)
   */
  private toTroubleShootingString(value: unknown): string | null {
    return typeof value === 'string' ? value : null;
  }

  /**
   * SYRC-6904: dispatch async survey creation from a route and wait (via subscription) for the
   * worker's terminal status, correlating on jobId. Handles the races the reviewer flagged on #2071:
   *   • a terminal status can arrive BEFORE the dispatch ack returns (fast job) — buffer it and
   *     re-evaluate once the jobId is known, rather than dropping it;
   *   • if no status ever arrives (e.g. an undelivered worker callback), a timeout surfaces a
   *     NON-TERMINAL message and stops the leak — never a false "failed";
   *   • the in-progress toast is shown only after a successful jobId ack, so a dispatch failure
   *     doesn't flash in-progress immediately followed by an error.
   */
  private async createSurveyFromRouteAsync(
    input: SurveyCreateInput,
  ): Promise<void> {
    const userId =
      this.store.selectSnapshot(AuthenticateUserState.getSyrcUserId) ?? '';
    const idToken =
      this.store.selectSnapshot(AuthenticateUserState.getIdToken) ?? '';

    let dispatchedJobId: string | null = null;
    let settled = false;
    let bufferedStatus: SurveyCreationStatus | null = null;
    let timeoutHandle: ReturnType<typeof setTimeout> | null = null;

    const finish = () => {
      settled = true;
      if (timeoutHandle) {
        clearTimeout(timeoutHandle);
        timeoutHandle = null;
      }
      statusSub.unsubscribe();
    };

    const applyTerminalStatus = (status: SurveyCreationStatus) => {
      if (settled) {
        return;
      }
      finish();
      if (status.status?.toLocaleLowerCase() === SUCCESS) {
        this.toastr.showSuccess(CREATE_SURVEY_SUCCESS, TOASTER_SUCCESS);
        this.router.navigate([REDIRECT_SURVEY_OVERVIEW]);
      } else {
        this.toastr.showError(status.message || FAILED_CREATE_SURVEY, ERROR);
      }
    };

    const statusSub = this.executeSurveyCreationSubscription(
      userId,
      idToken,
    ).subscribe({
      next: (status) => {
        if (settled) {
          return;
        }
        // The dispatch ack may not have returned yet (fast job). Buffer the latest event and
        // re-evaluate once dispatchedJobId is known, instead of dropping it.
        if (!dispatchedJobId) {
          bufferedStatus = status;
          return;
        }
        if (status.jobId !== dispatchedJobId) {
          return; // a different job (subscription is keyed only by userId)
        }
        applyTerminalStatus(status);
      },
      error: () => {
        if (settled) {
          return;
        }
        finish();
        this.toastr.showError(FAILED_CREATE_SURVEY, ERROR);
      },
    });

    const response = await this.createSurveyFromRoute(input);
    dispatchedJobId = response?.createSurveyFromRoute?.jobId ?? null;

    // Dispatch itself failed (validation / network) — no async status will ever arrive.
    if (!dispatchedJobId) {
      if (!settled) {
        finish();
        this.toastr.showError(FAILED_CREATE_SURVEY, ERROR);
      }
      return;
    }

    // Acked — now (and only now) show in-progress, so a dispatch failure never flashes it.
    this.toastr.showSuccess(CREATE_SURVEY_IN_PROGRESS, TOASTER_SUCCESS);

    // Replay a status that completed before the ack returned. (Cast resets the control-flow narrowing:
    // bufferedStatus is only ever assigned inside the subscribe callback, which TS doesn't track here.)
    const pending = bufferedStatus as SurveyCreationStatus | null;
    if (!settled && pending) {
      if (pending.jobId === dispatchedJobId) {
        applyTerminalStatus(pending);
        return;
      }
      bufferedStatus = null; // belonged to a different job; keep listening
    }

    // No terminal status yet — wait, then fall back to a NON-TERMINAL message (the job may still be
    // running or may have succeeded with an undelivered status; never report it as failed here).
    if (!settled) {
      timeoutHandle = setTimeout(() => {
        if (settled) {
          return;
        }
        finish();
        this.toastr.showInfo(CREATE_SURVEY_STILL_PROCESSING, INFO);
      }, SURVEY_CREATION_STATUS_TIMEOUT_MS);
    }
  }

  /**
   * SYRC-6904: fire the async createSurveyFromRoute mutation (returns a jobId ack immediately).
   */
  public async createSurveyFromRoute(
    input: SurveyCreateInput,
  ): Promise<CreateSurveyFromRouteMutation | null> {
    try {
      const variables: CreateSurveyFromRouteMutationVariables = { input };
      const response =
        await this.awsService.executeMutation<CreateSurveyFromRouteMutation>(
          createSurveyFromRoute,
          variables,
        );
      return response || null;
    } catch (error) {
      console.error(CREATE_SURVEY_ERROR, error);
      return null;
    }
  }

  /**
   * SYRC-6904: subscribe to survey-creation status for the current user (mirrors the bulk-import
   * status subscription in ImportService). Returns an RxJS Observable wrapping the Amplify stream.
   */
  public executeSurveyCreationSubscription(
    userId: string,
    idToken: string,
  ): Observable<SurveyCreationStatus> {
    const client = generateClient();
    const subscriptionObservable = client.graphql({
      query: onUpdateSurveyCreationStatus,
      variables: { userId },
      authToken: idToken,
    }) as { subscribe: (observer: unknown) => { unsubscribe: () => void } };

    return new Observable<SurveyCreationStatus>((observer) => {
      const subscription = subscriptionObservable.subscribe({
        next: (response: {
          data?: { onUpdateSurveyCreationStatus?: SurveyCreationStatus };
        }) => {
          const data = response?.data?.onUpdateSurveyCreationStatus;
          if (data) {
            observer.next(data);
          }
        },
        error: (error: unknown) => observer.error(error),
        complete: () => observer.complete(),
      });

      return () => subscription.unsubscribe();
    });
  }

  async createSurveyUpdateInput(
    newEntry: UpdateSurveyOverview,
    assets: Assets[],
  ): Promise<void> {
    const dueDate = new Date(newEntry.dueDate);
    dueDate.setHours(0, 0, 0, 0);
    const assignedToValues = {
      id: newEntry.assignedTo,
      name: newEntry.assignedToValue,
    };
    const inputObject: SurveyUpdateInput = {
      id: newEntry.surveyId,
      pk: newEntry.surveyPk,
      sk: newEntry.surveySk,
      surveyName: newEntry.surveyName,
      surveyType: newEntry.surveyType,
      surveyPriority: newEntry.surveyPriority,
      dueDate: dueDate.toISOString(),
      dataCollectionType: newEntry.dataCollectionType,
      surveyFrequencyType: newEntry.surveyFrequencyType,
      surveyDescription: newEntry.surveyDescription,
      assignedTo: assignedToValues,
      troubleShooting: this.toTroubleShootingString(newEntry.troubleshooting),
      filter: newEntry.filter,
      gridFilters: newEntry.gridFilters ?? null,
      surveyAssociatedAssetInput: assets.map((asset) => ({
        assetId: asset.id ?? '',
        level1ValueId: asset.level1Id ?? '',
        level2ValueId: asset.level2Id ?? '',
        level3ValueId: asset.level3Id ?? '',
        order: asset.order ?? null,
      })),
    };
    const response = await this.updateSurvey(inputObject);
    if (response?.updateSurvey?.status?.toLocaleLowerCase() == SUCCESS) {
      this.toastr.showSuccess(UPDATE_SURVEY_SUCCESS, TOASTER_SUCCESS);
      this.router.navigate([REDIRECT_SURVEY_OVERVIEW]);
    } else {
      this.toastr.showError(FAILED_CREATE_SURVEY, ERROR);
    }
  }

  // public async getAllSurvey(
  //   input: SurveyFilterInput,
  // ): Promise<SurveyConnection | null> {
  //   const query = getAllSurvey; // Ensure this is your GraphQL query string or document
  //   const variables: GetAllSurveyQueryVariables = {
  //     input: input,
  //   };

  //   try {
  //     const result = await this.awsService.executeGetQuery<{
  //       getAllSurvey: SurveyConnection;
  //     }>(query, variables);
  //     return result.getAllSurvey || null;
  //   } catch (error) {
  //     console.error(ERROR_FETCHING_SURVEY_OVERVIEW, error);
  //     return null;
  //   }
  // }

  public async getAllSurveyLaunchpad(
    input: SurveyFilterInput,
  ): Promise<SurveyConnection | null> {
    const query = getAllSurveyLaunchpad; // Ensure this is your GraphQL query string or document
    const variables: GetAllSurveyLaunchpadQueryVariables = {
      input: input,
    };

    try {
      const result = await this.awsService.executeGetQuery<{
        getAllSurveyLaunchpad: SurveyConnection;
      }>(query, variables);
      return result.getAllSurveyLaunchpad || null;
    } catch (error) {
      console.error(ERROR_FETCHING_SURVEY_OVERVIEW, error);
      return null;
    }
  }

  public async createSurvey(
    input: SurveyCreateInput,
  ): Promise<CreateSurveyMutation | null> {
    try {
      const variables: CreateSurveyMutationVariables = { input };
      const response =
        await this.awsService.executeMutation<CreateSurveyMutation>(
          createSurvey,
          variables,
        );
      return response || null;
    } catch (error) {
      console.error(CREATE_SURVEY_ERROR, error);
      return null;
    }
  }

  public async updateSurvey(
    input: SurveyUpdateInput,
  ): Promise<UpdateSurveyMutation | null> {
    try {
      const variables: UpdateSurveyMutationVariables = { input };
      const response =
        await this.awsService.executeMutation<UpdateSurveyMutation>(
          updateSurvey,
          variables,
        );
      return response || null;
    } catch (error) {
      console.error(CREATE_SURVEY_ERROR, error);
      return null;
    }
  }

  public async getSurveyName(
    primaryCompanyId: string,
    surveyName: string,
  ): Promise<Survey | null> {
    try {
      const variables: GetSurveyByNameQueryVariables = {
        primaryCompanyId,
        surveyName,
      };
      const query = getSurveyByName;
      const response =
        await this.awsService.executeGetQuery<GetSurveyByNameQuery>(
          query,
          variables,
        );
      return response.getSurveyByName || null;
    } catch (error) {
      console.error('Error Fetching Survey', error);
      return null;
    }
  }

  public async assignUserToSurvey(
    input: AssignUserToSurveyInput,
  ): Promise<SurveyResponse | null> {
    try {
      const variables: AssignUserToSurveyMutationVariables = { input };
      const response =
        await this.awsService.executeMutation<AssignUserToSurveyMutation>(
          assignUserToSurvey,
          variables,
        );
      return response.assignUserToSurvey || null;
    } catch (error) {
      console.error(ASSIGN_USER_TO_SURVEY_ERROR, error);
      return null;
    }
  }

  public async renameSurvey(
    input: RenameSurveyInput,
  ): Promise<RenameSurveyMutation | null> {
    try {
      const variables: RenameSurveyMutationVariables = { input };
      const response =
        await this.awsService.executeMutation<RenameSurveyMutation>(
          renameSurvey,
          variables,
        );
      return response || null;
    } catch (error) {
      console.error('Error renaming survey', error);
      return null;
    }
  }

  public async deleteSurvey(
    input: ResetSurveyInput,
  ): Promise<DeleteSurveyMutation | null> {
    try {
      const variables: DeleteSurveyMutationVariables = { input };
      const response =
        await this.awsService.executeMutation<DeleteSurveyMutation>(
          deleteSurvey,
          variables,
        );
      return response || null;
    } catch (error) {
      console.error('Error deleting survey', error);
      return null;
    }
  }
}
